/**
 * Application State Manager
 * Provides reactive state management with proper cleanup and memory management
 */

/**
 * @typedef {Object} AppState
 * @property {boolean} isInitialized - Whether the app is fully initialized
 * @property {boolean} isOnline - Whether the app has network connectivity
 * @property {boolean} hasLocation - Whether location is available
 * @property {boolean} solarDataAvailable - Whether solar calculations are available
 * @property {Date|null} lastCalculationTime - When solar data was last calculated
 * @property {string|null} errorMessage - Current error message, if any
 * @property {boolean} isRunning - Whether the app is currently running
 * @property {boolean} isPaused - Whether the app is paused (hidden/background)
 * @property {LocationStatus} locationStatus - Current location service status
 * @property {Object} performance - Performance metrics
 * @property {Object} battery - Battery optimization state
 */

/**
 * @typedef {Object} StateChangeEvent
 * @property {string} property - The property that changed
 * @property {*} oldValue - Previous value
 * @property {*} newValue - New value
 * @property {Date} timestamp - When the change occurred
 */

/**
 * Application State Manager with reactive updates and proper cleanup
 */
export class AppStateManager {
  constructor() {
    // Internal state object
    this._state = {
      isInitialized: false,
      isOnline: navigator.onLine,
      hasLocation: false,
      solarDataAvailable: false,
      lastCalculationTime: null,
      errorMessage: null,
      isRunning: false,
      isPaused: false,
      locationStatus: 'pending',
      performance: {
        startTime: Date.now(),
        updateCount: 0,
        errorCount: 0,
        locationRequests: 0,
        calculationRequests: 0,
        cacheHits: 0,
        cacheMisses: 0
      },
      battery: {
        level: null,
        charging: false,
        lowPowerMode: false
      }
    };

    // State change listeners
    this._listeners = new Map();
    this._globalListeners = new Set();
    
    // Cleanup tracking
    this._cleanupTasks = new Set();
    this._isDestroyed = false;
    
    // Debounce timers for batch updates
    this._updateTimers = new Map();
    this._batchedUpdates = new Map();
    
    // State history for debugging (limited to last 50 changes)
    this._stateHistory = [];
    this._maxHistorySize = 50;
    
    // Bind methods
    this.destroy = this.destroy.bind(this);
    this._handleBeforeUnload = this._handleBeforeUnload.bind(this);
    
    // Set up cleanup on page unload
    window.addEventListener('beforeunload', this._handleBeforeUnload);
  }

  /**
   * Get current state (immutable copy)
   * @returns {AppState} Current application state
   */
  getState() {
    if (this._isDestroyed) {
      console.warn('Attempting to get state from destroyed AppStateManager');
      return null;
    }
    
    // Return deep copy to prevent external mutations
    return this._deepClone(this._state);
  }

  /**
   * Get a specific state property
   * @param {string} property - Property name
   * @returns {*} Property value
   */
  get(property) {
    if (this._isDestroyed) {
      console.warn('Attempting to get property from destroyed AppStateManager');
      return null;
    }
    
    return this._getNestedProperty(this._state, property);
  }

  /**
   * Set a state property with reactive updates
   * @param {string} property - Property name (supports nested properties with dot notation)
   * @param {*} value - New value
   * @param {boolean} [immediate=false] - Whether to trigger updates immediately
   */
  set(property, value, immediate = false) {
    if (this._isDestroyed) {
      console.warn('Attempting to set property on destroyed AppStateManager');
      return;
    }

    const oldValue = this._getNestedProperty(this._state, property);
    
    // Only update if value actually changed
    if (this._isEqual(oldValue, value)) {
      return;
    }

    // Update the state
    this._setNestedProperty(this._state, property, value);
    
    // Record state change
    const changeEvent = {
      property,
      oldValue: this._deepClone(oldValue),
      newValue: this._deepClone(value),
      timestamp: new Date()
    };
    
    this._recordStateChange(changeEvent);
    
    // Trigger updates
    if (immediate) {
      this._notifyListeners(changeEvent);
    } else {
      this._scheduleUpdate(changeEvent);
    }
  }

  /**
   * Update multiple properties atomically
   * @param {Object} updates - Object with property-value pairs
   * @param {boolean} [immediate=false] - Whether to trigger updates immediately
   */
  update(updates, immediate = false) {
    if (this._isDestroyed) {
      console.warn('Attempting to update destroyed AppStateManager');
      return;
    }

    const changes = [];
    
    // Apply all updates and collect changes
    for (const [property, value] of Object.entries(updates)) {
      const oldValue = this._getNestedProperty(this._state, property);
      
      if (!this._isEqual(oldValue, value)) {
        this._setNestedProperty(this._state, property, value);
        
        const changeEvent = {
          property,
          oldValue: this._deepClone(oldValue),
          newValue: this._deepClone(value),
          timestamp: new Date()
        };
        
        changes.push(changeEvent);
        this._recordStateChange(changeEvent);
      }
    }
    
    // Notify listeners of all changes
    if (changes.length > 0) {
      if (immediate) {
        changes.forEach(change => this._notifyListeners(change));
      } else {
        changes.forEach(change => this._scheduleUpdate(change));
      }
    }
  }

  /**
   * Subscribe to state changes for a specific property
   * @param {string} property - Property name to watch
   * @param {function} callback - Callback function (oldValue, newValue, property) => void
   * @returns {function} Unsubscribe function
   */
  subscribe(property, callback) {
    if (this._isDestroyed) {
      console.warn('Attempting to subscribe to destroyed AppStateManager');
      return () => {};
    }

    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    // Initialize listeners array for this property if needed
    if (!this._listeners.has(property)) {
      this._listeners.set(property, new Set());
    }

    const listeners = this._listeners.get(property);
    listeners.add(callback);

    // Return unsubscribe function
    const unsubscribe = () => {
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this._listeners.delete(property);
        }
      }
    };

    // Track cleanup
    this._cleanupTasks.add(unsubscribe);

    return unsubscribe;
  }

  /**
   * Subscribe to all state changes
   * @param {function} callback - Callback function (changeEvent) => void
   * @returns {function} Unsubscribe function
   */
  subscribeAll(callback) {
    if (this._isDestroyed) {
      console.warn('Attempting to subscribe to destroyed AppStateManager');
      return () => {};
    }

    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    this._globalListeners.add(callback);

    // Return unsubscribe function
    const unsubscribe = () => {
      this._globalListeners.delete(callback);
    };

    // Track cleanup
    this._cleanupTasks.add(unsubscribe);

    return unsubscribe;
  }

  /**
   * Get state change history for debugging
   * @param {number} [limit=10] - Number of recent changes to return
   * @returns {StateChangeEvent[]} Array of recent state changes
   */
  getHistory(limit = 10) {
    if (this._isDestroyed) {
      return [];
    }
    
    return this._stateHistory.slice(-limit);
  }

  /**
   * Clear state change history
   */
  clearHistory() {
    this._stateHistory = [];
  }

  /**
   * Get performance metrics
   * @returns {Object} Performance metrics
   */
  getPerformanceMetrics() {
    if (this._isDestroyed) {
      return null;
    }
    
    return {
      ...this._state.performance,
      uptime: Date.now() - this._state.performance.startTime,
      listenerCount: Array.from(this._listeners.values()).reduce((sum, set) => sum + set.size, 0) + this._globalListeners.size,
      historySize: this._stateHistory.length,
      memoryUsage: this._getMemoryUsage()
    };
  }

  /**
   * Destroy the state manager and clean up all resources
   */
  destroy() {
    if (this._isDestroyed) {
      return;
    }

    console.log('Destroying AppStateManager...');

    // Clear all update timers
    for (const timer of this._updateTimers.values()) {
      clearTimeout(timer);
    }
    this._updateTimers.clear();
    this._batchedUpdates.clear();

    // Execute all cleanup tasks
    for (const cleanup of this._cleanupTasks) {
      try {
        cleanup();
      } catch (error) {
        console.warn('Cleanup task failed:', error);
      }
    }
    this._cleanupTasks.clear();

    // Clear all listeners
    this._listeners.clear();
    this._globalListeners.clear();

    // Clear history
    this._stateHistory = [];

    // Remove event listeners
    window.removeEventListener('beforeunload', this._handleBeforeUnload);

    // Mark as destroyed
    this._isDestroyed = true;

    console.log('AppStateManager destroyed');
  }

  /**
   * Handle page unload
   * @private
   */
  _handleBeforeUnload() {
    this.destroy();
  }

  /**
   * Schedule a batched update
   * @private
   * @param {StateChangeEvent} changeEvent - Change event
   */
  _scheduleUpdate(changeEvent) {
    const { property } = changeEvent;
    
    // Store the latest change for this property
    this._batchedUpdates.set(property, changeEvent);
    
    // Clear existing timer for this property
    if (this._updateTimers.has(property)) {
      clearTimeout(this._updateTimers.get(property));
    }
    
    // Schedule new update
    const timer = setTimeout(() => {
      const latestChange = this._batchedUpdates.get(property);
      if (latestChange) {
        this._notifyListeners(latestChange);
        this._batchedUpdates.delete(property);
      }
      this._updateTimers.delete(property);
    }, 16); // ~60fps batching
    
    this._updateTimers.set(property, timer);
  }

  /**
   * Notify listeners of state changes
   * @private
   * @param {StateChangeEvent} changeEvent - Change event
   */
  _notifyListeners(changeEvent) {
    if (this._isDestroyed) {
      return;
    }

    const { property, oldValue, newValue } = changeEvent;

    // Notify property-specific listeners
    const propertyListeners = this._listeners.get(property);
    if (propertyListeners) {
      for (const callback of propertyListeners) {
        try {
          callback(oldValue, newValue, property);
        } catch (error) {
          console.error('State listener error:', error);
        }
      }
    }

    // Notify global listeners
    for (const callback of this._globalListeners) {
      try {
        callback(changeEvent);
      } catch (error) {
        console.error('Global state listener error:', error);
      }
    }
  }

  /**
   * Record state change in history
   * @private
   * @param {StateChangeEvent} changeEvent - Change event
   */
  _recordStateChange(changeEvent) {
    this._stateHistory.push(changeEvent);
    
    // Limit history size
    if (this._stateHistory.length > this._maxHistorySize) {
      this._stateHistory.shift();
    }
  }

  /**
   * Get nested property value
   * @private
   * @param {Object} obj - Object to get property from
   * @param {string} path - Property path (dot notation)
   * @returns {*} Property value
   */
  _getNestedProperty(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Set nested property value
   * @private
   * @param {Object} obj - Object to set property on
   * @param {string} path - Property path (dot notation)
   * @param {*} value - Value to set
   */
  _setNestedProperty(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    
    const target = keys.reduce((current, key) => {
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      return current[key];
    }, obj);
    
    target[lastKey] = value;
  }

  /**
   * Deep clone an object
   * @private
   * @param {*} obj - Object to clone
   * @returns {*} Cloned object
   */
  _deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    
    if (obj instanceof Array) {
      return obj.map(item => this._deepClone(item));
    }
    
    const cloned = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = this._deepClone(obj[key]);
      }
    }
    
    return cloned;
  }

  /**
   * Check if two values are equal (deep comparison)
   * @private
   * @param {*} a - First value
   * @param {*} b - Second value
   * @returns {boolean} Whether values are equal
   */
  _isEqual(a, b) {
    if (a === b) return true;
    
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }
    
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }
    
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    
    if (keysA.length !== keysB.length) {
      return false;
    }
    
    for (const key of keysA) {
      if (!keysB.includes(key) || !this._isEqual(a[key], b[key])) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Get memory usage information
   * @private
   * @returns {Object|null} Memory usage info
   */
  _getMemoryUsage() {
    if ('memory' in performance) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      };
    }
    return null;
  }
}